import { useState, type ReactNode } from "react";
import { Check, History, Sparkles } from "lucide-react";
import {
  MOMENTO_DELEGADO,
  MOMENTO_INICIAL,
  momentoAlDeshacer,
  momentoContado,
  momentoSegunRespuesta,
  puedeEnviarRelato,
  resumenDeMomento,
  type MomentoMetaAds,
} from "../datos/estrategiaMetaAds";
import { CuadroPrompt } from "./CuadroPrompt";
import { PieDePaso, type PropsPasoPuestaEnMarcha } from "./PieDePaso";
import { CLASE_FOCO } from "./tarjetasFuente";

/**
 * Paso 3 de la Puesta en marcha: LA ESTRATEGIA DE META ADS.
 *
 * Son tres pantallas encadenadas dentro del mismo paso, no tres pasos: lo que
 * se decide es UNA cosa (con qué contexto arma Atlas la estrategia) y las tres
 * son el camino para decidirla. Un paso del flujo es una parada del recorrido;
 * esto es una conversación de tres turnos.
 *
 *  1. LA PREGUNTA — "¿Ya venías haciendo Meta Ads?". Ramifica: el que dice que
 *     sí pasa a contarlo; el que dice que no se saltea el cuadro entero, porque
 *     pedirle que cuente cómo lo venía trabajando es pedirle que invente algo.
 *  2. EL CUADRO — "Contame cómo venías trabajando Meta Ads hasta ahora", con la
 *     misma cara que la barra del chat (`CuadroPrompt`) y, abajo, la salida
 *     grande: "Dejar que Atlas arme la estrategia por mí".
 *  3. LO DECIDIDO — qué quedó y cómo deshacerlo.
 *
 * En qué momento está y a dónde va cada botón lo decide `estrategiaMetaAds.ts`,
 * que es puro y está testeado: acá no hay banderas sueltas que puedan quedar en
 * una combinación que no significa nada ("delegó pero además escribió").
 *
 * COMO TODO EL BLOQUE, ESTO NO MANDA NADA A NINGÚN LADO. El relato vive en el
 * estado del componente; no hay backend de estrategia todavía.
 *
 * Ver ADR-2026-08-18-estudio-marketing-paso-estrategia-meta-ads.
 */

/** El ancho de la columna en las tres pantallas del paso. El mismo que usa el
 *  chat para su barra: un cuadro de prompt a todo el ancho de un escritorio deja
 *  renglones que no se leen. */
const CLASE_COLUMNA = "mx-auto w-full max-w-[640px]";

/* ── Piezas ─────────────────────────────────────────────────────────────── */

/** Una de las dos respuestas de la pregunta. La tarjeta entera es el botón. */
const Opcion = ({
  icono,
  titulo,
  detalle,
  onClick,
}: {
  icono: ReactNode;
  titulo: string;
  detalle: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`group flex flex-1 flex-col items-start gap-3 rounded-2xl border border-[#02062B]/[0.08] bg-white p-5 text-left transition-colors hover:border-[#1644EB] hover:bg-[#F7F9FC] ${CLASE_FOCO}`}
  >
    <span
      aria-hidden="true"
      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#02062B]/[0.08] bg-white text-[#1644EB]"
    >
      {icono}
    </span>
    <span>
      <span className="block text-[14px] font-semibold text-brand-black">{titulo}</span>
      <span className="mt-1 block text-[12.5px] leading-relaxed text-[#02062B]/[0.55]">
        {detalle}
      </span>
    </span>
  </button>
);

/* ── El paso ────────────────────────────────────────────────────────────── */

export const PasoMetaAds = ({
  siguiente,
  onContinuar,
  anterior,
  onVolver,
}: PropsPasoPuestaEnMarcha) => {
  const [momento, setMomento] = useState<MomentoMetaAds>(MOMENTO_INICIAL);
  /** Lo que hay escrito en el cuadro. Vive aparte del momento a propósito: se
   *  conserva al ir y volver de la pantalla resuelta, que es lo que hace que
   *  "Editar lo que escribí" devuelva el texto y no un cuadro en blanco. */
  const [relato, setRelato] = useState("");

  const enviarRelato = () => {
    const contado = momentoContado(relato);
    // `null` es el cuadro vacío. No se avanza: el botón ya está apagado, esto
    // cubre el Enter y cualquier camino que se sume después.
    if (contado) setMomento(contado);
  };

  const resumen = resumenDeMomento(momento);

  return (
    <div className="flex flex-1 flex-col">
      {/* Centrado en el alto que sobra entre el encabezado y el pie: las tres
          pantallas son cortas y ancladas arriba se veían perdidas. */}
      <div className="flex flex-1 flex-col justify-center py-8">
        {momento.paso === "pregunta" && (
          <div className={CLASE_COLUMNA}>
            <h2 className="text-center text-[17px] font-semibold text-brand-black">
              ¿Ya venías haciendo Meta Ads?
            </h2>
            <p className="mt-1.5 text-center text-[13px] leading-relaxed text-[#02062B]/[0.55]">
              Sirve para saber desde dónde arrancar: con historia atrás o de cero.
            </p>
            <div className="mt-6 flex gap-4">
              <Opcion
                icono={<History className="h-4 w-4" aria-hidden="true" />}
                titulo="Sí, ya hice campañas"
                detalle="Contame cómo venías trabajando y Atlas parte de ahí."
                onClick={() => setMomento(momentoSegunRespuesta(true))}
              />
              <Opcion
                icono={<Sparkles className="h-4 w-4" aria-hidden="true" />}
                titulo="Todavía no"
                detalle="Arrancamos de cero: Atlas arma la primera estrategia."
                onClick={() => setMomento(momentoSegunRespuesta(false))}
              />
            </div>
          </div>
        )}

        {momento.paso === "contame" && (
          <div className={CLASE_COLUMNA}>
            <h2 className="text-center text-[17px] font-semibold text-brand-black">
              Contame cómo venías trabajando Meta Ads hasta ahora
            </h2>
            <p className="mt-1.5 mb-6 text-center text-[13px] leading-relaxed text-[#02062B]/[0.55]">
              Qué campañas tenías, a quién le hablabas, qué funcionó y qué no. Con lo que te acordés
              alcanza.
            </p>

            <CuadroPrompt
              valor={relato}
              onCambiar={setRelato}
              onEnviar={enviarRelato}
              puedeEnviar={puedeEnviarRelato(relato)}
              placeholder="Ej.: tenía dos campañas de tráfico frío a la home y una de retargeting con catálogo. El retargeting andaba bien, el tráfico frío no."
              etiqueta="Contame cómo venías trabajando Meta Ads hasta ahora"
              autoFoco
            />

            {/* La salida grande, para el que no tiene ganas de escribir. Va
                DEBAJO del cuadro y no al lado: es la alternativa, no un igual. */}
            <button
              type="button"
              onClick={() => setMomento(MOMENTO_DELEGADO)}
              className={`group mt-4 flex w-full items-center gap-4 rounded-2xl border border-[#02062B]/[0.08] bg-white py-5 px-5 text-left transition-colors hover:border-[#1644EB] hover:bg-[#F7F9FC] ${CLASE_FOCO}`}
            >
              <span
                aria-hidden="true"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#02062B]/[0.08] bg-white text-[#1644EB]"
              >
                <Sparkles className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold text-brand-black">
                  Dejar que Atlas arme la estrategia por mí
                </span>
                <span className="mt-1 block text-[12.5px] leading-relaxed text-[#02062B]/[0.55]">
                  No hace falta que escribas nada: usa lo que sabe de tu marca, tus fuentes y tu
                  sistema de diseño.
                </span>
              </span>
            </button>
          </div>
        )}

        {momento.paso === "listo" && resumen && (
          <div className={CLASE_COLUMNA}>
            {/* `role="status"`: se llega acá apretando un botón que hace
                desaparecer lo que había, así que un lector de pantalla se
                quedaría sin saber qué pasó. */}
            <div role="status" className="text-center">
              <span
                aria-hidden="true"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#DDEFE7] text-[#1D7A57]"
              >
                <Check className="h-5 w-5" />
              </span>
              <h2 className="mt-3 text-[17px] font-semibold text-brand-black">{resumen.titulo}</h2>
              <p className="mt-1.5 text-[13px] leading-relaxed text-[#02062B]/[0.55]">
                {resumen.detalle}
              </p>
            </div>

            {momento.relato && (
              <blockquote className="mt-6 max-h-[220px] overflow-y-auto whitespace-pre-wrap rounded-2xl border border-[#02062B]/[0.08] bg-white py-4 px-5 text-[13px] leading-relaxed text-[#02062B]/[0.75]">
                {momento.relato}
              </blockquote>
            )}

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setMomento(momentoAlDeshacer(momento))}
                className={`rounded-xl py-2 px-4 text-[13px] font-medium text-brand-primary transition-opacity hover:opacity-70 ${CLASE_FOCO}`}
              >
                {resumen.textoDeshacer}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Nunca bloqueado, como los dos pasos anteriores: la decisión de este
          paso se puede dejar para después. */}
      <PieDePaso
        habilitado
        motivo={null}
        siguiente={siguiente}
        onContinuar={onContinuar}
        anterior={anterior}
        onVolver={onVolver}
      />
    </div>
  );
};

export default PasoMetaAds;
